import {
  BizNestClientError,
  Iaddress,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  Icustomer,
  IcustomerAnalytics,
  IdataArrayResponse,
  IdeleteOneResponse, IfilterProps,
  TcustomerRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';
import { CommerceUserBase } from './commerce-user-base.define';

/**
 * Represents a customer in the commerce system, extending the base commerce user functionality.
 * This class provides methods for managing customer data, including CRUD operations and analytics.
 *
 * @extends {CommerceUserBase} Provides common commerce user functionality
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Customer} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const customer = await Customer.getAll();
 *
 * const customer = await Customer.filterAll({
 *
 * });
 *
 * const customer = await Customer.bulkDelete({
 *
 * });
 *
 * const customer = await Customer.getOne('resourceId');
 *
 * const customer = await Customer.remove();
 *
 * ```
 */
export class Customer extends CommerceUserBase {
  /**
   * Base URL for customer-related API endpoints.
   * @private
   * @static
   * @readonly
   */
  private static readonly baseUrl = '/customer';

  /**
   * Additional addresses associated with the customer.
   * @readonly
   */
  otherAddresses?: Iaddress[];

  /**
   * Instance-specific base URL for customer API endpoints.
   * Overrides the baseUrl from the parent class.
   * @protected
   */
  override baseUrl = '/customer';

  /**
   * Creates a new Customer instance.
   *
   * @param {Icustomer} data - The customer data used to initialize the instance.
   * @throws {BizNestClientError} When no data is provided.
   * @throws {Error} When parent class validation fails.
   *
   * @example
   * const customerData = {
   *   _id: '123',
   *   // ... other required fields from Icustomer
   * };
   * const customer = new Customer(customerData);
   */
  constructor(data: Icustomer) {
    if (!data) {
      throw new BizNestClientError('No data provided');
    }
    super(data);
    this.otherAddresses = data.otherAddresses;
  }

  /**
   * Retrieves analytics data for customers based on the provided filter criteria.
   * This method provides insights and aggregated data about customers.
   *
   * @static
   * @param {IfilterProps} filter - The filter criteria for retrieving customer analytics.
   * @returns {Promise<IcustomerAnalytics>} A promise that resolves to the customer analytics data.
   * @throws {Error} When the API request fails or returns an error.
   *
   * @example
   * const filter = {
   *   dateRange: {
   *     start: '2023-01-01',
   *     end: '2023-12-31'
   *   }
   * };
   * const analytics = await Customer.analytics(filter);
   */
  static async analytics(filter: IfilterProps): Promise<IcustomerAnalytics> {
    const observer$ = CommerceClient.ehttp
      .makePost<IcustomerAnalytics>(`${Customer.baseUrl}/analytics`, filter);
    const analytics = await lastValueFrom(observer$);

    return analytics;
  }

  /**
   * Retrieves a paginated list of all customers.
   *
   * @static
   * @param {number} [offset=0] - The number of records to skip (for pagination).
   * @param {number} [limit=20] - The maximum number of records to return.
   * @returns {Promise<{ count: number, dataArr: Customer[] }>}
   * An object containing the total count and an array of Customer instances.
   * @throws {Error} When the API request fails or returns an error.
   *
   * @example
   * // Get first page of 20 customers
   * const { count, dataArr } = await Customer.getAll(0, 20);
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<Icustomer>>(`${Customer.baseUrl}?${query}`);
    const customers = await lastValueFrom(observer$);

    return {
      count: customers.count,
      dataArr: customers.data.map((val) => new Customer(val))
    };
  }

  /**
   * Filters customers based on the provided criteria.
   *
   * @static
   * @param {IfilterProps} filter - The filter criteria for retrieving customers.
   * @returns {Promise<{ count: number, dataArr: Customer[] }>}
   * An object containing the total count and an array of filtered Customer instances.
   * @throws {Error} When the API request fails or returns an error.
   *
   * @example
   * const filter = {
   *   search: 'John',
   *   status: 'active'
   * };
   * const { count, dataArr } = await Customer.filterAll(filter);
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<Icustomer>>(`${Customer.baseUrl}/filter`, filter);
    const customers = await lastValueFrom(observer$);

    return {
      count: customers.count,
      dataArr: customers.data.map((val) => new Customer(val))
    };
  }

  /**
   * Retrieves a single customer by their unique identifier.
   *
   * @static
   * @param {string} resourceId - The unique identifier of the customer to retrieve.
   * @returns {Promise<Customer>} A Customer instance for the requested customer.
   * @throws {Error} When the customer is not found or the API request fails.
   *
   * @example
   * const customer = await Customer.getOne('customer-123');
   */
  static async getOne(resourceId: string): Promise<Customer> {
    const observer$ = CommerceClient.ehttp
      .makeGet<Icustomer>(`${Customer.baseUrl}/${resourceId}`);
    const customer = await lastValueFrom(observer$);

    return new Customer(customer);
  }

  /**
   * Adds a new customer to the system.
   *
   * @static
   * @param {TcustomerRequest<true>} payload - The customer data to add.
   * @returns {Promise<Customer>} A new Customer instance for the created customer.
   * @throws {Error} When the customer creation fails or validation errors occur.
   *
   * @example
   * const newCustomer = await Customer.add({
   *   firstName: 'John',
   *   lastName: 'Doe',
   *   email: 'john.doe@example.com',
   *   // ... other required fields
   * });
   */
  static async add(payload: TcustomerRequest<true>): Promise<Customer> {
    const observer$ = CommerceClient.ehttp.makePost<Icustomer>(
      `${Customer.baseUrl}`,
      payload
    );

    return new Customer(await lastValueFrom(observer$));
  }

  /**
   * Removes multiple customers based on the provided criteria.
   *
   * @static
   * @param {IbulkDelete} payload - The criteria for selecting customers to delete.
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation.
   * @throws {Error} When the deletion fails or validation errors occur.
   *
   * @example
   * const result = await Customer.bulkDelete({
   *   ids: ['customer-1', 'customer-2']
   * });
   */
  static bulkDelete(payload: IbulkDelete): Promise<IbulkDeleteSuccessResponse> {
    const observer$ = CommerceClient.ehttp.makePost<IbulkDeleteSuccessResponse>(
      `${Customer.baseUrl}/bulk-delete`,
      payload
    );

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current customer's information.
   *
   * @param {TcustomerRequest} payload - The updated customer data.
   * @returns {Promise<Customer>} A new Customer instance with the updated data.
   * @throws {Error} When the update fails or validation errors occur.
   *
   * @example
   * const updatedCustomer = await customer.update({
   *   firstName: 'Updated Name',
   *   // ... other fields to update
   * });
   */
  async update(payload: TcustomerRequest): Promise<Customer> {
    const observer$ = CommerceClient.ehttp.makePut<Icustomer>(
      `${this.baseUrl}/${this._id}`,
      payload
    );

    return new Customer(await lastValueFrom(observer$));
  }

  /**
   * Removes the current customer from the system.
   *
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion operation.
   * @throws {Error} When the deletion fails or the customer is not found.
   *
   * @example
   * const result = await customer.remove();
   * if (result?.success) {
   *   console.log('Customer removed successfully');
   * }
   */
  remove(): Promise<IdeleteOneResponse | null> {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${this.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
